'use client';

import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import { MediaUploader, type MediaFile, type UploadResult } from '@kit/uploads';

import { resolveLivestreamEmbed } from '../../lib/livestream-embed';
import type { ApplicationFieldConfig } from '../../types/audition.types';
import {
  buildRequiredRule,
  toPersistedMediaFile,
} from './dynamic-form-renderer-shared';

/**
 * One video entry per category. Matches the competition wizard's storage shape
 * exactly: `{ category, media_id, external_url, title }` — so the structure-
 * driven form persists identical data (BAL-269 / US-119 gap 2).
 */
export interface VideoCategoryEntry {
  category: string;
  media_id?: string | null;
  external_url?: string | null;
  title?: string | null;
  /**
   * The uploaded media object, retained so the uploader can re-display the
   * video on revisit/edit (same pattern as the single `file`/`video` field).
   * `media_id` is the canonical reference the booklet/exports read.
   */
  media?: MediaFile | null;
}

interface VideoCategoriesFieldProps {
  field: ApplicationFieldConfig;
  form: UseFormReturn<Record<string, unknown>>;
  /** Enforced when `field.validation.required` — blocks Next/Submit on empty. */
  requiredMessage: string;
  uploadFile?: (formData: FormData) => Promise<UploadResult>; // i18n-ignore TS generic type, not UI prose
  deleteFile?: (params: { mediaId: string }) => Promise<UploadResult>; // i18n-ignore TS generic type, not UI prose
}

/**
 * Fixed-category video repeater. The categories come from the field's
 * `options` (value = category key, label = display). Each category accepts
 * EITHER an uploaded video OR an external URL, plus an optional title — the
 * same either/or model as the legacy wizard. Stores a `VideoCategoryEntry[]`
 * keyed by category, so no entry is created until a category has content.
 */
export function VideoCategoriesField({
  field,
  form,
  requiredMessage,
  uploadFile,
  deleteFile,
}: VideoCategoriesFieldProps) {
  const { t } = useTranslation();
  const categories = field.options ?? [];

  return (
    <FormField
      control={form.control}
      name={field.key}
      // A required videos field must have at least one category filled: the
      // stored value is a VideoCategoryEntry[] and an entry only exists once a
      // category has content, so `isPresent([])` (empty) blocks Next/Submit.
      // Without this the `*` marker rendered but nothing enforced it.
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => {
        const entries: VideoCategoryEntry[] = Array.isArray(formField.value)
          ? (formField.value as VideoCategoryEntry[])
          : [];

        const entryFor = (category: string): VideoCategoryEntry | undefined =>
          entries.find((e) => e.category === category);

        // Upsert a category entry; drop it entirely when it has neither a
        // media id nor an external URL (keeps the array clean, like the wizard).
        const setEntry = (
          category: string,
          patch: Partial<VideoCategoryEntry>,
        ) => {
          const existing = entryFor(category) ?? { category };
          const next: VideoCategoryEntry = { ...existing, ...patch, category };
          const isEmpty =
            !next.media_id &&
            (!next.external_url || next.external_url.trim() === '');
          const without = entries.filter((e) => e.category !== category);
          formField.onChange(isEmpty ? without : [...without, next]);
        };

        return (
          <FormItem>
            <FormLabel>
              {field.label}
              {field.validation?.required && (
                <span className="text-destructive ml-1">*</span>
              )}
            </FormLabel>
            {field.description && (
              <FormDescription>{field.description}</FormDescription>
            )}
            <FormControl>
              <div className="space-y-4 pt-1">
                {categories.map((cat) => {
                  const entry = entryFor(cat.value);
                  // Re-display the uploaded video on revisit from the retained
                  // MediaFile (falls back to empty when only a bare id exists).
                  const mediaValue: MediaFile[] = entry?.media
                    ? [entry.media]
                    : [];
                  // Derived from the field value each render, so the embed
                  // updates live as the pasted URL changes. `null` for an empty
                  // or non-allowlisted URL — we just don't show a player then.
                  const embed = entry?.external_url
                    ? resolveLivestreamEmbed(entry.external_url)
                    : null;
                  return (
                    <div
                      key={cat.value}
                      className="space-y-3 rounded-lg border p-3"
                    >
                      <div className="text-sm font-medium">{cat.label}</div>

                      {uploadFile && !entry?.external_url && (
                        <MediaUploader
                          mode="single"
                          maxFileSize={field.validation?.maxFileSize}
                          allowedTypes={
                            field.validation?.fileTypes ?? ['video']
                          }
                          value={mediaValue}
                          uploadAction={uploadFile}
                          deleteAction={deleteFile}
                          onUploadComplete={(file) =>
                            setEntry(cat.value, {
                              media_id: file.id,
                              // Strip the transient object-URL `preview` before
                              // the MediaFile enters the persisted form value —
                              // same slimming the file/photo fields apply — so
                              // the answers blob stays small and never carries a
                              // stale `blob:` URL into the DB.
                              media: toPersistedMediaFile(file),
                            })
                          }
                          onDelete={() =>
                            setEntry(cat.value, { media_id: null, media: null })
                          }
                        />
                      )}

                      {!entry?.media_id && (
                        <div className="space-y-1.5">
                          <Label className="text-muted-foreground text-xs">
                            {t('auditions:videoCategories.orPasteLink')}
                          </Label>
                          <Input
                            type="url"
                            placeholder="https://youtube.com/… or vimeo.com/…"
                            value={entry?.external_url ?? ''}
                            onChange={(e) =>
                              setEntry(cat.value, {
                                external_url: e.target.value || null,
                              })
                            }
                          />
                        </div>
                      )}

                      {embed && (
                        <div className="aspect-video w-full overflow-hidden rounded-md border">
                          <iframe
                            src={embed.embedUrl}
                            title={entry?.title ?? cat.label}
                            className="size-full border-0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )}

                      {(entry?.media_id || entry?.external_url) && (
                        <Input
                          placeholder={t(
                            'auditions:videoCategories.titlePlaceholder',
                          )}
                          value={entry?.title ?? ''}
                          onChange={(e) =>
                            setEntry(cat.value, {
                              title: e.target.value || null,
                            })
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
